import type { ICustomControlProcedureReadProps } from '../../../interfaces/';
import { getServiceLabel } from '../../../../../../../common/customControls/getServiceLabel';
import { ProcessEntity } from '../../../../../../../common/enums/processEntity';
import { getDisplayName } from '../../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../../translate';
import { useEpisodeContext } from '../../../../../../utils/hooks/useEpisodeContext';
import { ProcedureReadButtons } from '../../../../../elements/carePathButtons/ProcedureReadButtons';
import { FormDivider, UserTextParagraph } from '../../../../../elements/common';
import { FormLabel } from '../../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../../elements/forms/InputContainer';
import { ProcessContainer } from '../../../../episodes/common';
import { ProcessItemFooter } from '../../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../../episodes/ProcessItemHeader';
import { ProfessionalSummaryDataRead } from '../../../../professionalSummaryData/ProfessionalSummaryDataRead';
import { CustomControlCollapseBody } from '../../../utils/v1/CustomControlCollapseBody';
import { MedicationsRead } from '../../../utils/v1/medications/MedicationsRead';
import { TxcsMedicationsList } from '../../../utils/v1/TxcsMedicationsList';
import { PilotExaminationV1ProcedureSummary } from './PilotExaminationV1ProcedureSummary';

export function PilotExaminationV1ProcedureRead(props: ICustomControlProcedureReadProps) {
    const { patient } = useEpisodeContext();

    return (
        <ProcessContainer>
            <ProcessItemHeader
                date={props.process.procedure && props.process.procedure.dateSent}
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                organizationName={
                    patient.authorization?.canSeePatientDetails && props.process.procedure?.performerOrganization?.name
                        ? props.process.procedure.performerOrganization.name
                        : undefined
                }
                practitionerName={
                    patient.authorization?.canSeePatientDetails && props.process.procedure?.performerPractitioner
                        ? getDisplayName(props.process.procedure.performerPractitioner)
                        : undefined
                }
                process={props.process}
                serviceType={props.process.serviceType}
                showProcessIcon
                title={getServiceLabel(props.process.code, 'procedure', props.process.name)}
            />

            <PilotExaminationV1ProcedureSummary {...props} />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                <FormDivider />

                {props.request.isDirectProcedureAdded && (
                    <>
                        <MedicationsRead
                            entity={ProcessEntity.procedure}
                            entityId={props.procedure.id}
                            patientUsesMedication={props.procedure.patientUsesMedication}
                        />
                        <ProfessionalSummaryDataRead request={props.request} />
                        <TxcsMedicationsList requestId={props.request.id} />
                    </>
                )}

                {props.procedure.customControlData.anamnese && (
                    <HorizontalFormGroupRead>
                        <FormLabel>{translateKey('carePlan.anamnese')}</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>{props.procedure.customControlData.anamnese}</UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {props.procedure.customControlData.toelichting && (
                    <HorizontalFormGroupRead>
                        <FormLabel>{translateKey('common.remarks')}</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>{props.procedure.customControlData.toelichting}</UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}
            </CustomControlCollapseBody>

            <ProcessItemFooter
                isItemOpen={props.itemState.isOpen}
                labelLess={translateKey('procedure.hideProcedure')}
                labelMore={translateKey('procedure.showProcedure')}
                onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
            />
            <ProcedureReadButtons {...props} />
        </ProcessContainer>
    );
}
